import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import profile from "../../../../Images/profile.jpg";
import { domain } from "../../../../security";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../../redux/IchthusSlice";
import Loader from "../../../loader/Loader";
import {
  IoMdArrowDown,
  IoMdArrowUp,
  IoMdCloseCircle,
  IoMdAdd,
  IoMdTrash,
  IoMdCheckmark,
  IoMdArrowDropright,
} from "react-icons/io";

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject("No file provided");
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const AddProducts = ({ onClose, refreshData, productToEdit }) => {
  const currentUserID = useSelector(selectUserID);

  // ── Stepper State ────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    productImage: null,
    productName: "",
    itemCode: "",
    barCode: "",
    description: "",
    hasSerial: false,
    isTrade: true,
    itemTypeId: null,
    itemType: "",
    itemTypeCategory: "", // ✅ Store if it is BOM or Regular
    isBom: false, // ✅ Save explicitly mapped boolean
    brandId: null,
    brand: "",
    categoryId: null,
    category: "",
    categoryTwoId: null,
    categoryTwo: "",
    categoryThreeId: null,
    categoryFourId: null,
    categoryFiveId: null,
    salesUomId: null,
    purchaseUomId: null,
    baseUomId: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(profile);
  const [showExtraCategories, setShowExtraCategories] = useState(false);

  // ── Dropdown data ────────────────────────────────────────
  const [itemTypes, setItemTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesTwo, setCategoriesTwo] = useState([]);
  const [categoriesThree, setCategoriesThree] = useState([]);
  const [categoriesFour, setCategoriesFour] = useState([]);
  const [categoriesFive, setCategoriesFive] = useState([]);
  const [uoms, setUoms] = useState([]);

  // ── UOM conversion state ─────────────────────────────────
  const [conversionList, setConversionList] = useState([]);
  const [newConversion, setNewConversion] = useState({
    fromUomId: "",
    toUomId: "",
    conversionRate: 1,
  });

  // ── Live label preview ───────────────────────────────────
  const resolvedItemType = useMemo(() => {
    const it = itemTypes.find((it) => it.id === Number(formData.itemTypeId));
    return it ? `${it.itemTypeName} (${it.category})` : "";
  }, [itemTypes, formData.itemTypeId]);

  const resolvedBrand = useMemo(
    () =>
      brands.find((b) => b.id === Number(formData.brandId))?.brandName || "",
    [brands, formData.brandId],
  );
  const resolvedCategory = useMemo(
    () =>
      categories.find((c) => c.id === Number(formData.categoryId))
        ?.categoryName || "",
    [categories, formData.categoryId],
  );
  const resolvedCategoryTwo = useMemo(
    () =>
      categoriesTwo.find((c) => c.id === Number(formData.categoryTwoId))
        ?.categoryTwoName || "",
    [categoriesTwo, formData.categoryTwoId],
  );

  // ── Fetch dropdowns ──────────────────────────────────────
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [
          uomsRes,
          brandsRes,
          catsRes,
          cats2Res,
          cats3Res,
          cats4Res,
          cats5Res,
          itemTypesRes,
        ] = await Promise.all([
          axios.get(`${domain}/api/UnitOfMeasurements`),
          axios.get(`${domain}/api/Brands`),
          axios.get(`${domain}/api/Categories`),
          axios.get(`${domain}/api/CategoriesTwo`),
          axios.get(`${domain}/api/CategoriesThree`),
          axios.get(`${domain}/api/CategoriesFour`),
          axios.get(`${domain}/api/CategoriesFive`),
          axios.get(`${domain}/api/ItemTypes`),
        ]);
        setUoms(uomsRes.data);
        setBrands(brandsRes.data);
        setCategories(catsRes.data);
        setCategoriesTwo(cats2Res.data);
        setCategoriesThree(cats3Res.data);
        setCategoriesFour(cats4Res.data);
        setCategoriesFive(cats5Res.data);
        setItemTypes(itemTypesRes.data);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };
    fetchDropdowns();
  }, []);

  // ── Populate form when editing ───────────────────────────
  useEffect(() => {
    const fetchFullProductDetails = async () => {
      if (productToEdit) {
        setIsLoading(true);
        try {
          const nonTradeValue =
            typeof productToEdit.nonTrade === "boolean"
              ? productToEdit.nonTrade
              : productToEdit.nonTrade === "true" ||
                productToEdit.nonTrade === true;
          const isTradeValue = !nonTradeValue;

          setFormData((prev) => ({
            ...prev,
            productName: productToEdit.productName || "",
            itemCode: productToEdit.itemCode || "",
            barCode: productToEdit.barCode || "",
            description: productToEdit.description || "",
            hasSerial: productToEdit.hasSerial || false,
            isTrade: isTradeValue,
            itemTypeId: productToEdit.itemTypeId || null,
            itemType: productToEdit.itemTypeName || "",
            itemTypeCategory: productToEdit.itemTypeCategory || "", // ✅ Load category
            isBom: productToEdit.isBom || false, // ✅ Load BOM flag
            brandId: productToEdit.brandId || null,
            brand: productToEdit.brandName || "",
            categoryId: productToEdit.categoryId || null,
            category: productToEdit.categoryName || "",
            categoryTwoId: productToEdit.categoryTwoId || null,
            categoryTwo: productToEdit.categoryTwoName || "",
            categoryThreeId: productToEdit.categoryThreeId || null,
            categoryFourId: productToEdit.categoryFourId || null,
            categoryFiveId: productToEdit.categoryFiveId || null,
            salesUomId: productToEdit.salesUomId || null,
            purchaseUomId: productToEdit.purchaseUomId || null,
            baseUomId: productToEdit.baseUomId || null,
          }));

          const response = await axios.get(
            `${domain}/api/Products/${productToEdit.id}`,
          );
          const fullProduct = response.data;

          const fullNonTradeValue =
            typeof fullProduct.nonTrade === "boolean"
              ? fullProduct.nonTrade
              : fullProduct.nonTrade === "true" ||
                fullProduct.nonTrade === true;
          const fullIsTradeValue = !fullNonTradeValue;

          if (fullProduct.productImage) {
            const imageSrc = fullProduct.productImage.startsWith("data:image")
              ? fullProduct.productImage
              : `data:image/jpeg;base64,${fullProduct.productImage}`;
            setPreviewImage(imageSrc);
            setFormData((prev) => ({
              ...prev,
              productImage: fullProduct.productImage,
              isTrade: fullIsTradeValue,
            }));
          } else {
            setPreviewImage(profile);
            setFormData((prev) => ({ ...prev, isTrade: fullIsTradeValue }));
          }

          // Load existing conversion rules
          await fetchUomConversions(productToEdit.id);
        } catch (error) {
          console.error("Error fetching full product details:", error);
          toast.error("Could not load full product details.");
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchFullProductDetails();
  }, [productToEdit]);

  // ── Stepper Navigation Handlers ──────────────────────────
  const handleNext = (e) => {
    if (e) e.preventDefault();

    // Custom robust validation instead of Native HTML validation
    if (!formData.productName.trim()) {
      toast.error("Product Name is required.");
      document.getElementById("productName")?.focus();
      return;
    }
    if (!formData.itemTypeId) {
      toast.error("Item Type is required.");
      document.getElementById("itemTypeId")?.focus();
      return;
    }
    if (!formData.baseUomId) {
      toast.error("Base UOM is required.");
      document.getElementById("baseUomId")?.focus();
      return;
    }

    setCurrentStep(2);
  };

  const handleBack = (e) => {
    if (e) e.preventDefault();
    setCurrentStep(1);
  };

  // ── UOM conversion helpers ───────────────────────────────
  const fetchUomConversions = async (productId) => {
    try {
      const res = await axios.get(`${domain}/api/UomConversions`);
      setConversionList(res.data.filter((c) => c.productId === productId));
    } catch (err) {
      console.error("Error fetching conversions", err);
    }
  };

  const handleAddConversion = () => {
    if (
      !newConversion.fromUomId ||
      !newConversion.toUomId ||
      !newConversion.conversionRate
    ) {
      toast.warning("Please fill all conversion fields.");
      return;
    }

    const baseUomId = Number(formData.baseUomId);
    const selectedToUomId = Number(newConversion.toUomId);
    const selectedFromUomId = Number(newConversion.fromUomId);
    let enteredRate = parseFloat(newConversion.conversionRate);
    let finalRate = enteredRate;
    let finalToUomId = selectedToUomId;

    if (selectedToUomId !== baseUomId) {
      const parentRule = conversionList.find(
        (c) => c.fromUomId === selectedToUomId && c.toUomId === baseUomId,
      );
      if (parentRule) {
        finalRate = enteredRate * parentRule.conversionRate;
        finalToUomId = baseUomId;
        toast.info(
          `Auto-calculated: 1 ${
            uoms.find((u) => u.id === selectedFromUomId)?.code
          } = ${finalRate} ${uoms.find((u) => u.id === baseUomId)?.code}`,
        );
      } else {
        toast.error(
          "Please define the conversion to the Base Unit first. (e.g., Define Kg → Pcs before defining Box → Kg)",
        );
        return;
      }
    }

    const fromUomObj = uoms.find((u) => u.id === selectedFromUomId);
    const toUomObj = uoms.find((u) => u.id === finalToUomId);
    const item = {
      id: null,
      fromUomId: selectedFromUomId,
      toUomId: finalToUomId,
      fromUomCode: fromUomObj?.code || "",
      toUomCode: toUomObj?.code || "",
      conversionRate: finalRate,
      productId: productToEdit ? productToEdit.id : null,
    };

    if (conversionList.some((c) => c.fromUomId === item.fromUomId)) {
      toast.error("A conversion rule for this unit already exists.");
      return;
    }

    setConversionList([...conversionList, item]);
    setNewConversion({ fromUomId: "", toUomId: "", conversionRate: 1 });
  };

  const handleRemoveConversion = async (index, id) => {
    if (id) {
      try {
        await axios.delete(`${domain}/api/UomConversions/${id}`);
        toast.success("Conversion deleted.");
      } catch {
        toast.error("Failed to delete conversion.");
        return;
      }
    }
    setConversionList((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Generic input handler ────────────────────────────────
  const handleInputChange = (e) => {
    const { id, value } = e.target;

    // Custom mapping for our dropdowns
    if (id === "itemTypeId") {
      const item = itemTypes.find((i) => i.id === Number(value));
      setFormData((prev) => ({
        ...prev,
        [id]: value || null,
        itemType: item ? item.itemTypeName : "",
        itemTypeCategory: item ? item.category : "",
        isBom: item ? item.category === "BOM" : false, // ✅ Set flag directly
      }));
    } else {
      const setDropdown = (list, nameField) => {
        const item = list.find((i) => i.id === Number(value));
        setFormData((prev) => ({
          ...prev,
          [id]: value || null,
          [nameField]: item
            ? item.brandName || item.categoryName || item.categoryTwoName || ""
            : "",
        }));
      };

      if (id === "brandId") setDropdown(brands, "brand");
      else if (id === "categoryId") setDropdown(categories, "category");
      else if (id === "categoryTwoId")
        setDropdown(categoriesTwo, "categoryTwo");
      else setFormData((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreviewImage(URL.createObjectURL(file));
      setFormData({ ...formData, productImage: file });
    }
  };

  // ── Submit ───────────────────────────────────────────────
  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();
    const baseUomId = Number(formData.baseUomId);
    const salesUomId = Number(formData.salesUomId);
    const purchaseUomId = Number(formData.purchaseUomId);

    if (!formData.itemTypeId) {
      toast.error("Item Type is required.");
      return;
    }

    if (!baseUomId) {
      toast.error("Base UOM is required.");
      return;
    }

    // Validate Sales UOM conversion
    if (salesUomId && salesUomId !== baseUomId) {
      if (!conversionList.some((c) => c.fromUomId === salesUomId)) {
        const salesName =
          uoms.find((u) => u.id === salesUomId)?.code || "Sales";
        const baseName = uoms.find((u) => u.id === baseUomId)?.code || "Base";
        toast.error(
          `Sales UOM (${salesName}) differs from Base UOM (${baseName}). Please add a conversion rule.`,
        );
        return;
      }
    }

    // Validate Purchase UOM conversion
    if (purchaseUomId && purchaseUomId !== baseUomId) {
      if (!conversionList.some((c) => c.fromUomId === purchaseUomId)) {
        const purchaseName =
          uoms.find((u) => u.id === purchaseUomId)?.code || "Purchase";
        const baseName = uoms.find((u) => u.id === baseUomId)?.code || "Base";
        toast.error(
          `Purchase UOM (${purchaseName}) differs from Base UOM (${baseName}). Please add a conversion rule.`,
        );
        return;
      }
    }

    setIsLoading(true);

    let imageToBeSaved = null;
    if (formData.productImage && typeof formData.productImage !== "string") {
      const base64String = await toBase64(formData.productImage);
      imageToBeSaved = base64String.split(",")[1];
    }

    const productPayload = {
      productName: formData.productName,
      itemCode: formData.itemCode,
      barCode: formData.barCode,
      description: formData.description,
      productImage: imageToBeSaved,
      hasSerial: formData.hasSerial,
      nonTrade: !formData.isTrade,
      isBom: formData.isBom, // ✅ Send mapped flag to API
      itemTypeId: formData.itemTypeId ? Number(formData.itemTypeId) : null,
      brandId: formData.brandId ? Number(formData.brandId) : null,
      categoryId: formData.categoryId ? Number(formData.categoryId) : null,
      categoryTwoId: formData.categoryTwoId
        ? Number(formData.categoryTwoId)
        : null,
      categoryThreeId: formData.categoryThreeId
        ? Number(formData.categoryThreeId)
        : null,
      categoryFourId: formData.categoryFourId
        ? Number(formData.categoryFourId)
        : null,
      categoryFiveId: formData.categoryFiveId
        ? Number(formData.categoryFiveId)
        : null,
      salesUomId: salesUomId || null,
      purchaseUomId: purchaseUomId || null,
      baseUomId: baseUomId || null,
      userID: currentUserID,
    };

    try {
      let productId = productToEdit?.id;

      if (productToEdit) {
        await axios.put(
          `${domain}/api/Products/${productToEdit.id}`,
          productPayload,
        );
        toast.success("Product updated successfully");
      } else {
        const res = await axios.post(`${domain}/api/Products`, productPayload);
        productId = res.data.id;
        toast.success("Product added successfully");
      }

      // Save new (unsaved) conversion rules
      const newConversions = conversionList
        .filter((c) => !c.id)
        .map((c) => ({
          productId,
          fromUomId: c.fromUomId,
          toUomId: c.toUomId,
          conversionRate: c.conversionRate,
        }));
      if (newConversions.length > 0) {
        await axios.post(`${domain}/api/UomConversions`, newConversions);
      }

      refreshData();
      onClose();
    } catch (error) {
      console.error("Error:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Style shortcuts ──────────────────────────────────────
  const inputClass =
    "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white";
  const labelClass = "block text-sm font-semibold text-slate-700 mb-1";
  const selectClass =
    "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white";

  // Label preview parts
  const labelParts = [
    { text: formData.productName, color: "text-slate-900" },
    { text: resolvedItemType, color: "text-purple-600" }, // ✅ Preview displays BOM/Regular format
    { text: resolvedBrand, color: "text-blue-600" },
    { text: resolvedCategory, color: "text-green-600" },
    { text: resolvedCategoryTwo, color: "text-orange-500" },
  ].filter((p) => p.text);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden transition-all"
        style={{ maxHeight: "95vh" }}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {productToEdit ? "Edit Product" : "Add New Product"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-red-500 transition"
            title="Close"
          >
            <IoMdCloseCircle size={28} />
          </button>
        </div>

        {/* ── Stepper Indicator ── */}
        <div className="py-4 px-4 bg-white border-b border-slate-100">
          <div className="flex items-center justify-center w-full max-w-2xl mx-auto">
            {/* Step 1 Circle */}
            <div className="flex flex-col items-center relative z-10">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                  currentStep >= 1
                    ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {currentStep > 1 ? <IoMdCheckmark size={18} /> : "1"}
              </div>
              <span
                className={`text-xs mt-1.5 font-semibold uppercase tracking-wider ${
                  currentStep >= 1 ? "text-blue-600" : "text-slate-500"
                }`}
              >
                Basic Info
              </span>
            </div>

            {/* Connecting Line */}
            <div
              className={`flex-1 h-1 mx-4 rounded-full transition-colors ${
                currentStep >= 2 ? "bg-blue-600" : "bg-slate-200"
              }`}
            ></div>

            {/* Step 2 Circle */}
            <div className="flex flex-col items-center relative z-10">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                  currentStep >= 2
                    ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                2
              </div>
              <span
                className={`text-xs mt-1.5 font-semibold uppercase tracking-wider ${
                  currentStep >= 2 ? "text-blue-600" : "text-slate-500"
                }`}
              >
                UOM Rules
              </span>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto p-6 relative bg-slate-50/50">
          {isLoading && <Loader />}

          {/* Note: The <form> tag's default action is disabled to prevent accidental Enter-key saves */}
          <form
            id="productForm"
            onSubmit={handleFormSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          >
            {/* ───────────────────────────────────────────────────────── */}
            {/* ── STEP 1: Basic Details ── */}
            {/* ───────────────────────────────────────────────────────── */}
            <div className={currentStep === 1 ? "block" : "hidden"}>
              {/* ── Label Preview ── */}
              <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                  Product Label Preview
                </p>
                <div className="bg-white border border-blue-100 rounded-lg px-4 py-3 min-h-[44px] flex items-center flex-wrap gap-1">
                  {labelParts.length > 0 ? (
                    labelParts.map((part, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && (
                          <span className="text-slate-300 font-light mx-1">
                            ·
                          </span>
                        )}
                        <span className={`text-sm font-semibold ${part.color}`}>
                          {part.text}
                        </span>
                      </React.Fragment>
                    ))
                  ) : (
                    <span className="text-sm text-slate-400 italic">
                      Start filling in the form to see the label…
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ── Left: Image + Toggles ── */}
                <div className="lg:col-span-1 flex flex-col items-center gap-4">
                  {/* Image upload */}
                  <div className="w-full aspect-square max-w-[220px] overflow-hidden rounded-xl border-2 border-dashed border-slate-300 relative group bg-white shadow-sm hover:border-blue-400 transition-colors">
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="object-cover w-full h-full"
                      onError={(e) => (e.target.src = profile)}
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                      <p className="text-white text-sm font-semibold">
                        Change Photo
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={isLoading}
                    />
                  </div>
                  <p className="text-xs text-slate-500 text-center">
                    JPEG, PNG or GIF
                  </p>

                  {/* Has Serial */}
                  <div className="w-full max-w-[220px] bg-white border border-slate-200 shadow-sm rounded-xl p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-3">
                      Serial Number?
                    </p>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div className="relative flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={formData.hasSerial}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              hasSerial: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-blue-600 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
                      </div>
                      <span className="text-sm text-slate-700 font-medium">
                        {formData.hasSerial
                          ? "Yes — Serialized"
                          : "No — By Quantity"}
                      </span>
                    </label>
                  </div>

                  {/* Trade / Non-Trade */}
                  <div className="w-full max-w-[220px] bg-white border border-slate-200 shadow-sm rounded-xl p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-3">
                      Product Classification
                    </p>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div className="relative flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={Boolean(formData.isTrade)}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              isTrade: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-orange-400 rounded-full peer peer-checked:bg-blue-600 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
                      </div>
                      <span className="text-sm text-slate-700 font-medium">
                        {formData.isTrade ? "Trade" : "Non-Trade"}
                      </span>
                    </label>
                  </div>
                </div>

                {/* ── Right: Fields ── */}
                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Product Name */}
                  <div className="sm:col-span-2">
                    <label className={labelClass}>
                      Product Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="productName"
                      type="text"
                      value={formData.productName}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g. Portland Cement Type I"
                    />
                  </div>

                  {/* Item Type */}
                  <div className="sm:col-span-2">
                    <label className={labelClass}>
                      Item Type <span className="text-red-500">*</span>
                      <span
                        className="ml-1.5 inline-block w-2 h-2 rounded-full bg-purple-600 align-middle"
                        title="Shows in label"
                      />
                    </label>
                    <select
                      id="itemTypeId"
                      value={formData.itemTypeId || ""}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">Select Item Type</option>
                      {itemTypes.map((it) => (
                        <option key={it.id} value={it.id}>
                          {/* ✅ Output clearly states the category attached to itemType */}
                          {it.itemTypeName} [{it.category}]
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Item Code (read-only) */}
                  <div>
                    <label className={labelClass}>
                      Item Code
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {productToEdit
                          ? "(locked — auto ID)"
                          : "(auto on save)"}
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        id="itemCode"
                        type="text"
                        value={formData.itemCode || ""}
                        readOnly
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500 bg-slate-50 cursor-not-allowed select-none"
                        placeholder="Auto-generated after save"
                      />
                      <svg
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Barcode */}
                  <div>
                    <label className={labelClass}>Barcode</label>
                    <input
                      id="barCode"
                      type="text"
                      value={formData.barCode || ""}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="EAN-13 or custom"
                    />
                  </div>

                  {/* Brand */}
                  <div>
                    <label className={labelClass}>
                      Brand
                      <span
                        className="ml-1.5 inline-block w-2 h-2 rounded-full bg-blue-600 align-middle"
                        title="Shows in label"
                      />
                    </label>
                    <select
                      id="brandId"
                      value={formData.brandId || ""}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">Select Brand</option>
                      {brands.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.brandName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label className={labelClass}>
                      Category
                      <span
                        className="ml-1.5 inline-block w-2 h-2 rounded-full bg-green-600 align-middle"
                        title="Shows in label"
                      />
                    </label>
                    <select
                      id="categoryId"
                      value={formData.categoryId || ""}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">Select Category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category Two */}
                  <div className="sm:col-span-2">
                    <label className={labelClass}>
                      Category Two
                      <span
                        className="ml-1.5 inline-block w-2 h-2 rounded-full bg-orange-400 align-middle"
                        title="Shows in label"
                      />
                    </label>
                    <select
                      id="categoryTwoId"
                      value={formData.categoryTwoId || ""}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">Select Category Two</option>
                      {categoriesTwo.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.categoryTwoName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Categories 3–5 toggle */}
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={() =>
                        setShowExtraCategories(!showExtraCategories)
                      }
                      className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 transition"
                    >
                      {showExtraCategories ? (
                        <IoMdArrowUp />
                      ) : (
                        <IoMdArrowDown />
                      )}
                      {showExtraCategories ? "Hide" : "Show"} Categories 3 – 5
                    </button>

                    {showExtraCategories && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                        <div>
                          <label className={labelClass}>Category Three</label>
                          <select
                            id="categoryThreeId"
                            value={formData.categoryThreeId || ""}
                            onChange={handleInputChange}
                            className={selectClass}
                          >
                            <option value="">None</option>
                            {categoriesThree.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.categoryThreeName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>Category Four</label>
                          <select
                            id="categoryFourId"
                            value={formData.categoryFourId || ""}
                            onChange={handleInputChange}
                            className={selectClass}
                          >
                            <option value="">None</option>
                            {categoriesFour.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.categoryFourName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>Category Five</label>
                          <select
                            id="categoryFiveId"
                            value={formData.categoryFiveId || ""}
                            onChange={handleInputChange}
                            className={selectClass}
                          >
                            <option value="">None</option>
                            {categoriesFive.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.categoryFiveName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* UOMs Selection */}
                  <div className="sm:col-span-2 border-t border-slate-200 pt-5 mt-2">
                    <p className="text-sm font-semibold text-slate-800 mb-3">
                      Units of Measurement Setup
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className={labelClass}>
                          Base UOM <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="baseUomId"
                          value={formData.baseUomId || ""}
                          onChange={handleInputChange}
                          className={selectClass}
                        >
                          <option value="">Select Base Unit</option>
                          {uoms.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.code}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Sales UOM</label>
                        <select
                          id="salesUomId"
                          value={formData.salesUomId || ""}
                          onChange={handleInputChange}
                          className={selectClass}
                        >
                          <option value="">Select Sales Unit</option>
                          {uoms.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.code}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Purchase UOM</label>
                        <select
                          id="purchaseUomId"
                          value={formData.purchaseUomId || ""}
                          onChange={handleInputChange}
                          className={selectClass}
                        >
                          <option value="">Select Purchase Unit</option>
                          {uoms.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.code}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="sm:col-span-2 border-t border-slate-200 pt-5 mt-2">
                    <label className={labelClass}>Description / Notes</label>
                    <textarea
                      id="description"
                      rows={3}
                      value={formData.description || ""}
                      onChange={handleInputChange}
                      className={inputClass + " resize-none"}
                      placeholder="Optional notes or specifications…"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={currentStep === 2 ? "block" : "hidden"}>
              <div className="max-w-4xl mx-auto">
                <div className="mb-5">
                  <h3 className="text-lg font-semibold text-slate-800">
                    Unit Conversions
                  </h3>

                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    Specify conversion rates to your base unit{" "}
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                      {uoms.find((u) => u.id === Number(formData.baseUomId))
                        ?.code || "Base"}
                    </span>
                    .
                    <span className="block mt-1 text-xs text-slate-400">
                      This step is optional.
                    </span>
                  </p>
                </div>

                {/* Add row UI */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-4">
                    Add Conversion Rule
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                    <div>
                      <label className={labelClass}>From Unit</label>{" "}
                      <select
                        value={newConversion.fromUomId}
                        onChange={(e) =>
                          setNewConversion({
                            ...newConversion,
                            fromUomId: e.target.value,
                          })
                        }
                        className={selectClass}
                      >
                        <option value="">Select Unit</option>
                        {uoms
                          .filter((u) => u.id !== Number(formData.baseUomId)) // Prevents 1 Base to 1 Base duplicate rules
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              1 {u.code}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>To Unit (Base)</label>
                      <select
                        value={newConversion.toUomId}
                        onChange={(e) =>
                          setNewConversion({
                            ...newConversion,
                            toUomId: e.target.value,
                          })
                        }
                        className={selectClass}
                      >
                        <option value="">Select Unit</option>
                        {uoms
                          .filter((u) => u.id === Number(formData.baseUomId))
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.code} (Base)
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Conversion Rate</label>
                      <input
                        type="number"
                        step="0.001"
                        value={newConversion.conversionRate}
                        onChange={(e) =>
                          setNewConversion({
                            ...newConversion,
                            conversionRate: e.target.value,
                          })
                        }
                        className={inputClass}
                        placeholder="1.00"
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={handleAddConversion}
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg shadow transition"
                      >
                        <IoMdAdd size={18} /> Add Rule
                      </button>
                    </div>
                  </div>
                </div>

                {/* Conversion List Table */}
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-4 font-semibold text-slate-700">
                          Defined Conversions
                        </th>
                        <th className="px-4 py-4 font-semibold text-slate-700 text-right">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {conversionList.length === 0 ? (
                        <tr>
                          <td
                            colSpan={2}
                            className="px-4 py-10 text-center text-slate-400 text-sm italic"
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-base font-medium">
                                No rules defined
                              </span>
                              <span className="text-xs">
                                Add a conversion rule to get started.
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        conversionList.map((item, index) => (
                          <tr
                            key={item.id || index}
                            className="hover:bg-slate-50 transition-colors"
                          >
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2 font-medium">
                                <span className="text-slate-500 text-xs uppercase">
                                  1
                                </span>
                                <span className="text-slate-900 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-sm shadow-sm">
                                  {item.fromUomCode}
                                </span>
                                <IoMdArrowDropright
                                  className="text-slate-400 mx-1"
                                  size={18}
                                />
                                <span className="text-emerald-600 font-bold text-base">
                                  {item.conversionRate}
                                </span>
                                <span className="text-slate-700 ml-1">
                                  {item.toUomCode}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveConversion(index, item.id)
                                }
                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                                title="Remove rule"
                              >
                                <IoMdTrash size={18} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </form>
        </div>
        {/* ── Footer / Navigation ── */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center gap-3">
          {/* Left Button */}
          {currentStep === 1 ? (
            <button
              onClick={onClose}
              type="button"
              className="px-6 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition"
            >
              Cancel
            </button>
          ) : (
            <button
              onClick={handleBack}
              type="button"
              className="px-6 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition"
            >
              Back
            </button>
          )}

          {/* Right Button */}
          {currentStep === 1 ? (
            <button
              onClick={handleNext}
              type="button"
              className="px-8 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleFormSubmit}
              disabled={isLoading}
              className="px-8 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg shadow transition disabled:opacity-50"
            >
              {isLoading
                ? "Saving…"
                : productToEdit
                  ? "Save Changes"
                  : "Add Product"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddProducts;
